/**
 * Audio Service for ElevenLabs Conversational AI
 * Handles real-time audio capture, processing, and playback
 * Following ElevenLabs WebSocket API specifications
 */

class AudioService {
  constructor() {
    // Core audio components
    this.audioContext = null;
    this.mediaStream = null;
    this.sourceNode = null;
    this.analyser = null;
    this.processor = null;
    
    // Audio settings (ElevenLabs specs: PCM 16kHz)
    this.sampleRate = 16000;
    this.bufferSize = 4096; // Process audio in 4K chunks for real-time performance
    
    // State management
    this.isMuted = false;
    this.isSpeakerOn = true;
    this.isUserSpeaking = false;
    this.isInitialized = false;
    
    // Real-time audio streaming (ElevenLabs handles VAD server-side)
    this.isStreaming = false;
    this.streamingBuffer = [];
    this.chunkSize = 1600; // 100ms chunks at 16kHz (optimal for real-time streaming)
    this.maxBufferSize = 8000; // Max 500ms buffer before force sending
    
    // Audio playback management (prevent overlapping)
    this.currentAudioSource = null;
    this.audioQueue = [];
    this.isPlayingAudio = false;
    
    // Callbacks
    this.onAudioData = null;
    this.onUserSpeakingStart = null;
    this.onUserSpeakingEnd = null;
    
  }

  /**
   * Initialize audio service with microphone access
   * @returns {Promise<void>}
   */
  async initialize() {
    
    try {
      // Request microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Create AudioContext with 16kHz sample rate for ElevenLabs
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: this.sampleRate
      });

      // Create audio processing chain
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;
      this.analyser.smoothingTimeConstant = 0.3;

      // Create ScriptProcessor for real-time audio processing
      this.processor = this.audioContext.createScriptProcessor(this.bufferSize, 1, 1);
      this.processor.onaudioprocess = (event) => this.processAudioData(event);

      // Connect the audio processing chain
      this.sourceNode.connect(this.analyser);
      this.analyser.connect(this.processor);
      this.processor.connect(this.audioContext.destination);

      this.isInitialized = true;

    } catch (error) {
      console.error('❌ Failed to initialize audio service:', error);
      throw new Error(`Audio initialization failed: ${error.message}`);
    }
  }

  /**
   * Process real-time audio data - ElevenLabs continuous streaming approach
   * @param {AudioProcessingEvent} event
   */
  processAudioData(event) {
    if (this.isMuted || !this.onAudioData) {
      return;
    }

    const inputBuffer = event.inputBuffer;
    const inputData = inputBuffer.getChannelData(0);
    
    // Add audio data to streaming buffer
    this.streamingBuffer.push(...inputData);
    
    // Send audio in real-time chunks (ElevenLabs expects continuous stream)
    this.processStreamingAudio();
  }

  /**
   * Process streaming audio in real-time chunks
   * Send audio continuously but with basic noise filtering
   */
  processStreamingAudio() {
    // Send chunks when we have enough data
    while (this.streamingBuffer.length >= this.chunkSize) {
      const chunk = this.streamingBuffer.splice(0, this.chunkSize);
      const chunkArray = new Float32Array(chunk);
      
      // Basic audio level check - don't send pure silence
      const rms = this.calculateRMS(chunkArray);
      if (rms > 0.001) { // Very low threshold to allow quiet speech
        this.sendAudioChunk(chunkArray, 'streaming');
      } else {
        // Still send occasionally to maintain connection, but much less frequently
        this.silenceChunkCount = (this.silenceChunkCount || 0) + 1;
        if (this.silenceChunkCount % 10 === 0) { // Every 1 second of silence
          this.sendAudioChunk(chunkArray, 'silence_keepalive');
        }
      }
    }
    
    // Force send if buffer gets too large (prevent memory buildup)
    if (this.streamingBuffer.length >= this.maxBufferSize) {
      const chunk = this.streamingBuffer.splice(0);
      if (chunk.length > 0) {
        this.sendAudioChunk(new Float32Array(chunk), 'overflow');
      }
    }
  }

  /**
   * Calculate RMS for audio level monitoring (not for VAD)
   * @param {Float32Array} audioData
   * @returns {number}
   */
  calculateRMS(audioData) {
    let sum = 0;
    for (let i = 0; i < audioData.length; i++) {
      sum += audioData[i] * audioData[i];
    }
    return Math.sqrt(sum / audioData.length);
  }

  /**
   * Detect if user is speaking (for UI feedback only, not for sending logic)
   * @param {Float32Array} audioData
   */
  updateSpeakingStatus(audioData) {
    const rms = this.calculateRMS(audioData);
    const isCurrentlySpeaking = rms > 0.01; // Simple threshold for UI feedback
    
    if (isCurrentlySpeaking && !this.isUserSpeaking) {
      this.isUserSpeaking = true;
      this.onUserSpeakingStart?.();
    } else if (!isCurrentlySpeaking && this.isUserSpeaking) {
      // Use a small delay to avoid flickering
      setTimeout(() => {
        if (this.calculateRMS(audioData) <= 0.01) {
          this.isUserSpeaking = false;
          this.onUserSpeakingEnd?.();
        }
      }, 500);
    }
  }

  /**
   * Send individual audio chunk to ElevenLabs (continuous streaming)
   * @param {Float32Array} audioData - Audio chunk to send
   * @param {string} reason - Why audio is being sent ('streaming', 'overflow')
   */
  sendAudioChunk(audioData, reason) {
    if (audioData.length === 0) {
      return;
    }

    try {
      // Convert to PCM16 as required by ElevenLabs
      const pcm16Data = this.convertToPCM16(audioData);
      
      // Send to WebSocket service
      this.onAudioData(pcm16Data);
      
      // Update speaking status for UI feedback
      this.updateSpeakingStatus(audioData);
      
    } catch (error) {
      console.error('❌ Error sending audio chunk:', error);
    }
  }

  /**
   * Convert Float32Array to PCM16 ArrayBuffer (ElevenLabs format)
   * @param {Float32Array} float32Array
   * @returns {ArrayBuffer}
   */
  convertToPCM16(float32Array) {
    // Resample if needed (though we should already be at 16kHz)
    let processedData = float32Array;
    if (this.audioContext.sampleRate !== this.sampleRate) {
      processedData = this.resampleAudio(float32Array, this.audioContext.sampleRate, this.sampleRate);
    }
    
    // Convert to PCM16
    const buffer = new ArrayBuffer(processedData.length * 2);
    const view = new DataView(buffer);
    
    for (let i = 0; i < processedData.length; i++) {
      // Clamp sample to [-1, 1] and convert to 16-bit integer
      const sample = Math.max(-1, Math.min(1, processedData[i]));
      const pcm16Sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(i * 2, pcm16Sample, true); // little-endian
    }
    
    return buffer;
  }

  /**
   * Simple linear resampling
   * @param {Float32Array} inputData
   * @param {number} inputSampleRate
   * @param {number} outputSampleRate
   * @returns {Float32Array}
   */
  resampleAudio(inputData, inputSampleRate, outputSampleRate) {
    if (inputSampleRate === outputSampleRate) {
      return inputData;
    }

    const ratio = inputSampleRate / outputSampleRate;
    const outputLength = Math.floor(inputData.length / ratio);
    const output = new Float32Array(outputLength);
    
    for (let i = 0; i < outputLength; i++) {
      const sourceIndex = Math.floor(i * ratio);
      output[i] = inputData[sourceIndex];
    }
    
    return output;
  }

  /**
   * Convert PCM16 ArrayBuffer to Float32Array for playback
   * @param {ArrayBuffer} pcm16
   * @returns {Float32Array}
   */
  convertFromPCM16(pcm16) {
    const view = new DataView(pcm16);
    const float32Array = new Float32Array(pcm16.byteLength / 2);
    
    for (let i = 0; i < float32Array.length; i++) {
      const pcm16Sample = view.getInt16(i * 2, true); // little-endian
      float32Array[i] = pcm16Sample / (pcm16Sample < 0 ? 0x8000 : 0x7FFF);
    }
    
    return float32Array;
  }

  /**
   * Play audio received from ElevenLabs (with overlap prevention)
   * @param {ArrayBuffer} audioData - PCM16 audio data
   */
  async playAudio(audioData) {
    if (!this.isSpeakerOn) {
      return;
    }

    // Add to queue and process
    this.audioQueue.push(audioData);
    this.processAudioQueue();
  }

  /**
   * Process audio queue to prevent overlapping
   */
  async processAudioQueue() {
    // If already playing, wait for current audio to finish
    if (this.isPlayingAudio) {
      return;
    }

    // Get next audio from queue
    const audioData = this.audioQueue.shift();
    if (!audioData) {
      return;
    }

    // Only process audio if service is initialized and connected
    if (!this.isInitialized || !this.audioContext || this.audioContext.state === 'closed') {
      return;
    }

    try {
      this.isPlayingAudio = true;
      
      // Convert PCM16 to Float32Array
      const float32Data = this.convertFromPCM16(audioData);
      
      // Create audio buffer
      const audioBuffer = this.audioContext.createBuffer(1, float32Data.length, this.sampleRate);
      audioBuffer.getChannelData(0).set(float32Data);
      
      // Stop any currently playing audio
      if (this.currentAudioSource) {
        try {
          this.currentAudioSource.stop();
          this.currentAudioSource.disconnect();
        } catch (e) {
          // Source might already be stopped
        }
        this.currentAudioSource = null;
      }
      
      // Create and configure source
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      this.currentAudioSource = source;
      
      // Create gain node for volume control
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 1.0;
      
      // Connect audio graph only if context is still valid
      if (this.audioContext.state !== 'closed') {
        source.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        // Handle playback completion
        source.onended = () => {
          this.isPlayingAudio = false;
          this.currentAudioSource = null;
          
          // Process next item in queue
          setTimeout(() => this.processAudioQueue(), 50); // Small delay to prevent rapid switching
        };
        
        // Start playback
        source.start(0);
      } else {
        this.isPlayingAudio = false;
        this.currentAudioSource = null;
      }
      
    } catch (error) {
      console.error('❌ Error playing audio:', error);
      this.isPlayingAudio = false;
      this.currentAudioSource = null;
      
      // Continue processing queue on error
      setTimeout(() => this.processAudioQueue(), 100);
    }
  }

  /**
   * Toggle microphone mute
   * @returns {boolean} New mute state
   */
  toggleMute() {
    this.isMuted = !this.isMuted;
    
    if (this.isMuted) {
      // Clear streaming buffer and reset speaking state
      this.streamingBuffer = [];
      this.isUserSpeaking = false;
      this.onUserSpeakingEnd?.();
    }
    
    return this.isMuted;
  }

  /**
   * Toggle speaker on/off
   * @returns {boolean} New speaker state
   */
  toggleSpeaker() {
    this.isSpeakerOn = !this.isSpeakerOn;
    return this.isSpeakerOn;
  }

  /**
   * Get current audio level (0-1)
   * @returns {number}
   */
  getAudioLevel() {
    if (!this.analyser) return 0;
    
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);
    
    // Calculate average
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    
    return (sum / dataArray.length) / 255;
  }

  /**
   * Set audio data callback
   * @param {Function} callback
   */
  setAudioDataCallback(callback) {
    this.onAudioData = callback;
  }

  /**
   * Set user speaking callbacks
   * @param {Function} onStart
   * @param {Function} onEnd
   */
  setUserSpeakingCallbacks(onStart, onEnd) {
    this.onUserSpeakingStart = onStart;
    this.onUserSpeakingEnd = onEnd;
  }

  /**
   * Set VAD threshold
   * @param {number} threshold
   */
  setVadThreshold(threshold) {
    this.vadThreshold = threshold;
  }

  /**
   * Clean up audio resources
   */
  async cleanup() {
    
    try {
      // Send any remaining audio in buffer
      if (this.streamingBuffer.length > 0) {
        const remainingAudio = new Float32Array(this.streamingBuffer);
        this.sendAudioChunk(remainingAudio, 'cleanup');
        this.streamingBuffer = [];
      }
      
      // Stop any playing audio and clear queue FIRST
      if (this.currentAudioSource) {
        try {
          this.currentAudioSource.stop();
          this.currentAudioSource.disconnect();
        } catch (e) {
          // Source might already be stopped
        }
        this.currentAudioSource = null;
      }
      this.audioQueue = [];
      this.isPlayingAudio = false;
      
      // Stop all audio tracks
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => {
          track.stop();
        });
        this.mediaStream = null;
      }
      
      // Disconnect audio nodes
      if (this.processor) {
        this.processor.disconnect();
        this.processor = null;
      }
      
      if (this.analyser) {
        this.analyser.disconnect();
        this.analyser = null;
      }
      
      if (this.sourceNode) {
        this.sourceNode.disconnect();
        this.sourceNode = null;
      }
      
      // Close audio context - this should free up system audio resources
      if (this.audioContext && this.audioContext.state !== 'closed') {
        // Suspend first to prevent any conflicts
        await this.audioContext.suspend();
        await this.audioContext.close();
        this.audioContext = null;
      }
      
      // Reset state
      this.isInitialized = false;
      this.isUserSpeaking = false;
      this.streamingBuffer = [];
      
      
    } catch (error) {
      console.error('❌ Error during audio cleanup:', error);
    }
  }

  /**
   * Suspend audio service without full cleanup (for navigating away)
   */
  async suspend() {
    if (this.audioContext && this.audioContext.state === 'running') {
      await this.audioContext.suspend();
    }
  }
}

// Export singleton instance
export const audioService = new AudioService();
export default audioService;